<template>
    <v-container>
        <div class="text-lg-right">
            <v-btn color="primary" text v-on:click="cancel">close</v-btn>
        </div>
        <v-card-title>
            <span class="headline">{{ headlineText }}</span>
        </v-card-title>
        <v-row v-if="warning_fileType || warning_fileSize || warning_fileDuplicate">
            <v-col cols="12" class="px-7 error--text">
                <div v-html="warning_message"></div>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-file-input
                    v-model="files"
                    :label="labelText"
                    :hint="hintText"
                    persistent-hint
                    :truncate-length="200"
                    :multiple="isMultiple"
                    :accept="file_typeMatch.join(',')"
                    @change="handleChange">
                </v-file-input>
            </v-col>
        </v-row>
         <v-row>
            <v-col cols="12">
                <v-progress-linear v-if="isUploading"
                    :indeterminate="true"
                />
            </v-col>
        </v-row>
        <v-card-actions>
            <div class="flex-grow-1"></div>
            <v-btn color="warning" text v-on:click="cancel">Cancel</v-btn>
            <v-btn color="primary" text v-on:click="upload" :disabled="disable_upload">Upload</v-btn>
        </v-card-actions>
    </v-container>
</template>

<script>
    import axios from "axios";
    import Cookie from "js-cookie";
    import appUrls from '../../helpers/environment';
    import { safeParseJson } from '../../helpers/safe-json';

    export default {
        name: 'FileUpload',
        props: [
            'item', 
            'fieldData', 
            'moduleKey', 
            'projectId', 
            'userId',
            'extraMsg'
        ],
        
        data: () => ({
            files: [],
            message: '',
            current_files: [],
            isUploading: false,
            disable_upload: false,
            warning_fileType: false,
            warning_fileSize: false,
            warning_fileDuplicate: false,
            warning_message: 'There seems to be an issue with the file being uploaded!',
            invalid_filetype_details: [],
            file_match: [],
            file_typeMatch: [],
            allowedFileTypes: [
                "application/octet-stream", // general
                "application/msword", // doc
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
                "application/vnd.ms-word.document.macroenabled.12", // docm on Mac
                "application/vnd.ms-word.document.macroEnabled.12", // docm on Windows
                "application/vnd.ms-excel", // xls
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
                "application/vnd.ms-excel.sheet.macroenabled.12", // xlsm on Mac
                "application/vnd.ms-excel.sheet.macroEnabled.12", // xlsm on Windows
                "application/vnd.openxmlformats-officedocument.presentationml.presentation", // pptx
                "application/pdf", // pdf
                "image/jpeg", // jpg
                "image/png", // png
            ],
            authUser: {
                id: String,
                group: Number
            },
            isPartner: false
        }),

        computed: {
            fileTypeText () {
                let file_type_text = String
                switch ( this.fieldData.props.fileType ) {
                    case 'xls':
                    case 'xlsx':
                    case 'xlsm':    
                        file_type_text = 'Accepted file types are: xls/xlsx/xlsm';
                        this.file_typeMatch = [
                            "application/vnd.ms-excel", // xls
                            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
                            "application/vnd.ms-excel.sheet.macroenabled.12", // xlsm on Mac
                            "application/vnd.ms-excel.sheet.macroEnabled.12", // xlsm on Windows
                        ]
                        break;

                    case 'doc':
                    case 'docx':
                    case 'docm':
                        file_type_text = 'Accepted file types are: doc/docx/docm';
                        this.file_typeMatch = [
                            "application/msword", // doc
                            "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
                            "application/vnd.ms-word.document.macroenabled.12", // docm on Mac
                            "application/vnd.ms-word.document.macroEnabled.12", // docm on Windows
                        ]
                        break;

                    case 'pptx':
                        file_type_text = 'Accepted file type is: pptx';
                        this.file_typeMatch = [
                            "application/vnd.openxmlformats-officedocument.presentationml.presentation", // pptx
                        ]
                        break;

                    case 'pdf':
                        file_type_text = 'Accepted file type is: pdf';
                        this.file_typeMatch = [
                            "application/pdf", // pdf
                        ]
                        break;
                    
                    case 'jpeg,png':
                        file_type_text = 'Accepted file type is: jpeg/png';
                        this.file_typeMatch = [
                            "image/jpeg", // jpg
                            "image/png", // png
                        ]
                        break;

                    default:
                        file_type_text = 'Accepted file types are: pdf, doc/docx, xls/xlsx, pptx, jpeg/png'; 
                        this.file_typeMatch = this.allowedFileTypes
                        break;
                }
                return file_type_text
            },

            isMultiple () {
                return this.fieldData.props.multiple ? true : false
            },
            
            hintText () {
                return this.fieldData.props.multiple ? `You can upload multiple files at once ... ${this.fileTypeText}` : `Upload only one file ... ${this.fileTypeText}`
            },
            
            labelText () {
                return this.fieldData.props.multiple ? 'Click to Select Files' : 'Click to Select a File'
            },
            
            headlineText () {
                return this.extraMsg != undefined ? this.extraMsg : this.fieldData.props.multiple ? 'Add Files' : 'Add A File'
            }
        },
        
        mounted: async function () {
            await this.fileCheck(this.fieldData, this.projectId )
            await this.checkUser()
        },
        
        methods: {
            checkUser: async function () {
                try {    
                    const user_check = await axios.get(`${appUrls.api}/api/user_check`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" }
                    })

                    if ( user_check.data.outcome ) {    
						this.authUser.id = user_check.data.user_data.id
                        this.isPartner = user_check.data.user_data.group == 'nm_partner_firm' ? true : false
                    } else {
						console.log(user_check.data.message)
					}
                } catch ( error ) {
                    console.log(error);
                }
			},

            fileCheck: async function (fieldData, projectId) {
                try {                    
                    const file_check = await axios.post(`${appUrls.api}/api/file_check`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        fieldData, 
                        projectId
                    })

                    this.current_files = file_check.data.outcome && file_check.data.file_obj.length ? safeParseJson(file_check.data.file_obj, []) : []                    
                } catch (err) {
                    console.log(err)
                    return err
                }
            },

            handleChange: function () {
                if (this.files) {
                    this.warning_message = 'There seems to be an issue with the file being uploaded!'
                    this.invalid_filetype_details = []
                    this.file_match = []

                    const invalid_file = this.files.some( file => {
                        return  !this.allowedFileTypes.includes(file.type) || 
                                !this.file_typeMatch.includes(file.type) || 
                                file.size == 0 || 
                                this.current_files.some( current => current.filename == file.name )
                    })

                    if ( !invalid_file ) {
                        // Save a reference to the file blob.
                        // We will use this later for uploading.
                        this.disable_upload = false
                        this.warning_fileType = false
                        this.warning_fileDuplicate = false
                        // this.files = []
                    } else {
                        this.disable_upload = true

                        this.files.forEach( file => {
                            if ( !this.allowedFileTypes.includes(file.type) || !this.file_typeMatch.includes(file.type) ) {
                                this.warning_fileType = true
                                this.invalid_filetype_details.push(file)
                            }
                            
                            if ( file.size == 0 ) {
                                this.warning_fileSize = true
                            }

                            if ( this.current_files.some( current => current.filename == file.name ) ) {
                                this.current_files.forEach( current => {
                                    if ( current.filename == file.name ) {
                                        this.file_match.push(current.filename)
                                    }
                                })
                                this.warning_fileDuplicate = true
                            }
                        })
                        
                        if ( this.invalid_filetype_details.length ) { 
                            this.warning_message = `The file type <strong>${this.invalid_filetype_details[0].type}</strong> is not permitted for uploading! ${this.fileTypeText}`

                        }
                            
                        if ( this.warning_fileSize ) {
                            this.warning_message = `The file size is 0mb, please check the file and upload a file with a larger size.`
                        }

                        if ( this.warning_fileDuplicate ) {
                            if ( this.file_match.length == 1 ) {
                                this.warning_message = `This file: <strong>${this.file_match}</strong> seems to be a duplicate, please delete the file before uploading the new one.`
                            } else {
                                this.warning_message = `These files: <strong>${this.file_match.join(', ')}</strong> seem to be a duplicates, please delete these files before uploading the new ones.`
                            }
                        }   
                        
                        setTimeout(() => { 
                            this.files = []
                        }, 5000)
                    }
                }
            },

            upload: async function () {
                this.isUploading = true;

                const formData = new FormData(); // document.getElementById('form')
                this.files.forEach( file => {
                    return formData.append('files', file);
                })
                formData.append('authentication', Cookie.get('token'));
                formData.append('moduleKey', this.moduleKey);
                formData.append('field', this.fieldData.name);
                formData.append('projectId', this.projectId);
                formData.append('userId', this.userId);
                formData.append('isDeliverable', false);
                formData.append('isPartner', this.isPartner ? true : false);
                formData.append('isRevision', this.fieldData.name == 'revision' ? true : false);
                formData.append('source', 'client');
                // console.log(...formData)

                try {
                    const res = await axios.post(`${appUrls.api}/api/upload_file`, formData)
                    
                    if ( res.data.outcome === false ) {
                        throw new Error(res.data.message);
                    }

                    setTimeout(() => {
                        const files_uploaded_obj = res.data.uploaded_files.map( uploaded_file => {
                            // Update the provided item with the data from the upload
                            const file_name = uploaded_file.file_name.split('/');
                            return {
                                // filename and filesize are provided by the browser.  easy peasy.
                                fileid: uploaded_file.file_id,
                                filename: file_name[file_name.length - 1],
                                filesize: uploaded_file.file_size,
                                fileurl: uploaded_file.file_url,
                                fileuploaddate: uploaded_file.file_upload_date,
                                fileisdeliverable: uploaded_file.is_deliverable
                            }
                        })
                        // console.log(files_uploaded_obj)
                        Object.assign(this.item, files_uploaded_obj.reverse())

                        this.isUploading = false;
                        this.files = null;

                        // Trigger the confirm callback
                        this.$emit('confirm', this.item)
                    }, 1000)

                } catch (err) {
                    setTimeout(() => {
                        this.cancel()
                        // this.$emit('triggerUserFeedback', true, err.message );
                        console.log(err.message)
                    }, 2000)
                }
            },

            cancel: function () {
                // Reset the control on cancel so it loads fresh next time
                this.isUploading = false;
                this.files = null;

                // Trigger the cancel callback
                this.$emit('cancel');
            }
        }
    }
</script>


// WEBPACK FOOTER //
// src/components/data_table_forms/file-upload.vue